// PDF generators for Patrimônio module.
// - gerarPDFPatrimonioPorCliente: itens atualmente alocados a um cliente + histórico
// - gerarPDFPatrimonioPorFuncionario: itens sob posse de um funcionário + histórico
// - gerarPDFPatrimonioItem: ficha completa do item (status + timeline desde entrada no sistema)
//
// Reutiliza os tokens visuais compartilhados de /lib/pdfStyles.js e o mesmo
// algoritmo de paginação (optimizePagination + spacer invisível) do cronograma
// para garantir integridade dos cards entre páginas.

import html2pdf from "html2pdf.js";
import QRCode from "qrcode";
import { BRAND } from "@/lib/brand";
import { PDF_MARGINS, escapeHtml, fixLegacyText, safeText } from "@/lib/pdfStyles";

const SITUACAO_LABEL = {
  disponivel: "Disponível",
  em_obra: "Em Obra",
  em_posse_funcionario: "Com Funcionário",
  emprestado: "Emprestado",
  manutencao: "Manutenção",
  reparo_externo: "Reparo Externo",
  roubo: "Roubo",
  furto: "Furto",
  perda: "Perda",
  inoperante: "Inoperante",
  baixado: "Baixado",
};

const SITUACAO_COLOR = {
  disponivel: { bg: "#E8F5E9", color: "#2E7D32" },
  em_obra: { bg: "#E3F2FD", color: "#1565C0" },
  em_posse_funcionario: { bg: "#E8EAF6", color: "#283593" },
  emprestado: { bg: "#F3E5F5", color: "#6A1B9A" },
  manutencao: { bg: "#FFF3E0", color: "#E65100" },
  reparo_externo: { bg: "#FFE0B2", color: "#BF360C" },
  roubo: { bg: "#FFEBEE", color: "#B71C1C" },
  furto: { bg: "#FFEBEE", color: "#B71C1C" },
  perda: { bg: "#FFEBEE", color: "#B71C1C" },
  inoperante: { bg: "#EEEEEE", color: "#424242" },
  baixado: { bg: "#F5F5F5", color: "#616161" },
};

function sanitize(str = "") {
  return String(str || "").replace(/[^a-zA-Z0-9-_.]/g, "_").slice(0, 60);
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    if (isNaN(dt.getTime())) return String(d).slice(0, 10);
    return dt.toLocaleDateString("pt-BR");
  } catch { return String(d).slice(0, 10); }
}
function fmtDateTime(d) {
  if (!d) return "—";
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    if (isNaN(dt.getTime())) return String(d);
    return dt.toLocaleString("pt-BR");
  } catch { return String(d); }
}
function fmtCurrency(v) {
  return (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function situacaoBadge(sit) {
  const cfg = SITUACAO_COLOR[sit] || { bg: "#E0E0E0", color: "#212121" };
  return `<span style="display:inline-block;padding:3px 10px;border-radius:999px;font-size:11px;font-weight:800;background:${cfg.bg};color:${cfg.color};text-transform:uppercase;letter-spacing:0.4px;font-family:'Arial Black',Arial,sans-serif">${SITUACAO_LABEL[sit] || sit}</span>`;
}

async function buildQR(url) {
  if (!url || typeof window === "undefined") return "";
  try {
    return await QRCode.toDataURL(url, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 240,
      color: { dark: "#1A237E", light: "#FFFFFF" },
    });
  } catch { return ""; }
}

const baseStyles = `
<style>
  @media print {
    @page { size: A4 portrait; margin: 15mm 10mm 15mm 10mm; }
  }
  * {
    box-sizing: border-box;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  .pdf-pat-root {
    font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
    font-size: 12px;
    color: #000000;
    width: 1080px;
    background: #ffffff;
    padding: 0 6px;
    line-height: 1.35;
  }
  .pdf-pat-root p { margin: 0; }
  .pdf-pat-root h1, .pdf-pat-root h2, .pdf-pat-root h3 {
    break-inside: avoid; page-break-inside: avoid;
  }
  .pdf-pat-root .card,
  .pdf-pat-root .item-block,
  .pdf-pat-root .contrato-info,
  .pdf-pat-root tr,
  .pdf-pat-root .timeline-event {
    break-inside: avoid; page-break-inside: avoid;
  }
  .pdf-pat-root .item-block { display: block; orphans: 3; widows: 3; }

  .pdf-pat-root .brand-bar { display: flex; height: 8px; margin-bottom: 18px; }
  .pdf-pat-root .brand-bar .b1 { flex: 3; background: #1A237E; }
  .pdf-pat-root .brand-bar .b2 { flex: 1; background: #D4A437; }

  .pdf-pat-root .header {
    display: flex; align-items: flex-start; gap: 14px; margin-bottom: 12px;
    padding: 0 4px;
  }
  .pdf-pat-root .header-left { display: flex; align-items: center; gap: 10px; flex: 1; }
  .pdf-pat-root .brand-logo { width: 56px; height: 56px; object-fit: contain; }
  .pdf-pat-root .brand-text .brand { font-size: 18px; font-weight: 900; color: #1A237E; margin: 0; font-family: 'Arial Black', Arial, sans-serif; }
  .pdf-pat-root .brand-text .brand-sub { font-size: 12px; color: #616161; margin: 0; }
  .pdf-pat-root .doc-meta { text-align: right; font-size: 11px; color: #424242; }
  .pdf-pat-root .doc-meta .num { font-size: 14px; color: #1A237E; font-weight: 800; }
  .pdf-pat-root .header-qr {
    display: flex; flex-direction: column; align-items: center; gap: 4px;
    margin-left: 14px; padding-left: 14px; border-left: 1.5px solid #E0E0E0;
  }
  .pdf-pat-root .header-qr .qr-img {
    width: 84px; height: 84px; display: block;
    border: 1.5px solid #1A237E; border-radius: 4px; background: #fff; padding: 2px;
  }
  .pdf-pat-root .header-qr .qr-caption {
    font-size: 10px; font-weight: 700; color: #1A237E; text-transform: uppercase;
    letter-spacing: 0.3px; font-family: Arial, sans-serif;
  }

  .pdf-pat-root h1 { font-size: 24px; font-weight: 900; color: #1A237E; margin: 8px 4px 4px; font-family: 'Arial Black', Arial, sans-serif; }
  .pdf-pat-root h2 { font-size: 16px; font-weight: 800; color: #1A237E; margin: 14px 4px 8px; padding-bottom: 6px; border-bottom: 2px solid #D4A437; font-family: 'Arial Black', Arial, sans-serif; }
  .pdf-pat-root h3 { font-size: 14px; font-weight: 700; color: #1A237E; margin: 10px 4px 6px; font-family: 'Arial Black', Arial, sans-serif; }

  .pdf-pat-root .contrato-info {
    background: #F5F5F5; border: 1px solid #BDBDBD; border-radius: 6px;
    padding: 12px 14px; margin: 0 4px 14px; font-size: 13px; color: #212121;
    display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px;
    line-height: 1.4;
  }
  .pdf-pat-root .contrato-info b { color: #1A237E; }

  .pdf-pat-root .item-block {
    border: 1.5px solid #BDBDBD; border-radius: 6px; padding: 10px 14px; margin: 0 4px 10px;
    background: #fff; font-size: 14px; color: #000000; text-align: left; line-height: 1.35;
  }
  .pdf-pat-root .item-block .item-header {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
    border-bottom: 1px solid #E0E0E0; padding-bottom: 6px; margin-bottom: 8px;
  }
  .pdf-pat-root .item-block .item-codigo {
    font-family: Consolas, 'Courier New', ui-monospace, monospace;
    font-size: 15px; color: #616161; font-weight: 800;
  }
  .pdf-pat-root .item-block .item-nome {
    font-size: 17px; font-weight: 800; color: #1A237E;
    font-family: 'Arial Black', Arial, sans-serif;
  }
  .pdf-pat-root .item-block .item-valor { margin-left: auto; font-size: 14px; color: #212121; font-weight: 800; font-family: Consolas, monospace; }
  .pdf-pat-root .item-block .item-meta {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px 20px;
    font-size: 14px; line-height: 1.4;
  }
  .pdf-pat-root .item-block .item-meta b { color: #1A237E; }
  .pdf-pat-root .item-block .item-obs {
    margin-top: 8px; padding-top: 8px; border-top: 1px dashed #BDBDBD;
    font-size: 13px; color: #424242; font-style: italic;
  }

  .pdf-pat-root table.hist {
    width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 8px;
  }
  .pdf-pat-root table.hist thead th {
    background: #1A237E; color: #fff; padding: 6px 10px; text-align: left;
    font-weight: 800; font-size: 12px; font-family: 'Arial Black', Arial, sans-serif;
  }
  .pdf-pat-root table.hist tbody td {
    padding: 6px 10px; border-bottom: 1px solid #E0E0E0; color: #000000;
    vertical-align: top; font-size: 13px;
  }
  .pdf-pat-root table.hist tbody tr.even td { background: #F5F5F5; }

  /* Timeline (usado em ficha individual do item) */
  .pdf-pat-root .timeline { margin: 0 4px; padding: 0; }
  .pdf-pat-root .timeline-event {
    display: grid; grid-template-columns: 160px 1fr; gap: 12px;
    padding: 10px 14px; border-left: 4px solid #1A237E; background: #FAFAFA;
    border-radius: 0 6px 6px 0; margin-bottom: 8px; font-size: 14px;
  }
  .pdf-pat-root .timeline-event.criacao { border-left-color: #2E7D32; background: #E8F5E9; }
  .pdf-pat-root .timeline-event.reparo { border-left-color: #E65100; background: #FFF3E0; }
  .pdf-pat-root .timeline-event.prejuizo { border-left-color: #B71C1C; background: #FFEBEE; }
  .pdf-pat-root .timeline-event.inativacao { border-left-color: #424242; background: #EEEEEE; }
  .pdf-pat-root .timeline-event .te-data {
    font-family: Consolas, 'Courier New', monospace; font-size: 13px;
    color: #1A237E; font-weight: 800;
  }
  .pdf-pat-root .timeline-event .te-titulo {
    font-family: 'Arial Black', Arial, sans-serif; font-size: 14px;
    color: #1A237E; margin-bottom: 3px;
  }
  .pdf-pat-root .timeline-event .te-detalhe { font-size: 13px; color: #212121; line-height: 1.4; }

  .pdf-pat-root .footer {
    margin: 20px 4px 0; padding-top: 10px; border-top: 2px solid #BDBDBD;
    display: flex; justify-content: space-between; font-size: 11px; color: #212121; font-weight: 600;
  }
  .pdf-pat-root .footer b { color: #1A237E; }
</style>
`;

function headerHtml(titulo, dataEmissao, qrDataUrl = "") {
  const logoSrc = BRAND.logoDataUrl || BRAND.logoUrl;
  return `
    <div class="brand-bar"><div class="b1"></div><div class="b2"></div></div>
    <div class="header">
      <div class="header-left">
        <img src="${logoSrc}" alt="OBRASUL-RJ" class="brand-logo" crossorigin="anonymous" />
        <div class="brand-text">
          <p class="brand">${BRAND.name}</p>
          <p class="brand-sub">${BRAND.tagline}</p>
        </div>
      </div>
      <div class="doc-meta">
        <div class="num">${escapeHtml(titulo)}</div>
        <div>Emitido em ${dataEmissao}</div>
      </div>
      ${qrDataUrl ? `
        <div class="header-qr">
          <img src="${qrDataUrl}" alt="QR" class="qr-img" />
          <div class="qr-caption">Acesse online</div>
        </div>
      ` : ''}
    </div>
  `;
}

function itemBlock(item) {
  const responsavel = item.responsavel_nome || item.responsavel_info?.nome || "—";
  const dataAquisicao = fmtDate(item.data_aquisicao);
  const valor = fmtCurrency(item.valor_aquisicao || 0);
  const localizacao = item.localizacao_descricao || item.localizacao_origem || "—";
  return `
    <div class="item-block">
      <div class="item-header">
        <span class="item-codigo">${safeText(item.codigo_patrimonio || "s/cod")}</span>
        <span class="item-nome">${safeText(fixLegacyText(item.nome))}</span>
        ${situacaoBadge(item.situacao)}
        <span class="item-valor">${valor}</span>
      </div>
      <div class="item-meta">
        <div><b>Categoria:</b> ${safeText(item.categoria || "—")}</div>
        <div><b>Marca/Modelo:</b> ${safeText(item.marca || "—")} ${safeText(item.modelo || "")}</div>
        <div><b>Adquirido em:</b> ${dataAquisicao}</div>
        <div><b>Responsável:</b> ${safeText(responsavel)}</div>
        <div><b>Localização:</b> ${safeText(fixLegacyText(localizacao))}</div>
        <div><b>Estado:</b> ${safeText(item.estado_conservacao || "—")}</div>
      </div>
      ${item.observacoes ? `<div class="item-obs">${safeText(item.observacoes)}</div>` : ''}
    </div>
  `;
}

function movimentacoesTable(movs) {
  if (!movs || movs.length === 0) {
    return '<p style="font-size:13px;color:#616161;margin:6px 4px;font-style:italic">Nenhuma movimentação registrada.</p>';
  }
  const rows = movs.map((m, idx) => {
    const destino = m.cliente_info?.nome || m.funcionario_info?.nome || m.emprestado_para || m.localizacao_descricao || "—";
    const responsavel = m.responsavel_info?.nome || m.recebedor_info?.name || "—";
    return `
      <tr class="${idx % 2 === 0 ? 'even' : ''}">
        <td style="width:110px">${fmtDate(m.created_at || m.data_solicitacao)}</td>
        <td style="width:150px">${SITUACAO_LABEL[m.tipo] || m.tipo}</td>
        <td>${safeText(destino)}</td>
        <td>${safeText(responsavel)}</td>
        <td>${safeText(m.motivo || m.localizacao_descricao || "—")}</td>
      </tr>
    `;
  }).join("");
  return `
    <table class="hist">
      <thead><tr>
        <th>Data</th><th>Movimentação</th><th>Destino</th><th>Responsável</th><th>Observações</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
  `;
}

// ============ optimizePagination (mesmo padrão do cronogramaPDF) ============
function optimizePagination(rootEl) {
  const pageHeightPx = 1517;
  const safetyMarginPx = 6;
  const blocks = Array.from(rootEl.querySelectorAll(".item-block, .timeline-event"));
  if (!blocks.length) return;
  const rootTop = rootEl.getBoundingClientRect().top + window.scrollY;
  blocks.forEach((block) => {
    const rect = block.getBoundingClientRect();
    const top = rect.top + window.scrollY - rootTop;
    const height = rect.height;
    if (height <= 0 || height >= pageHeightPx) return;
    const currentPageIndex = Math.floor(top / pageHeightPx);
    const currentPageEnd = (currentPageIndex + 1) * pageHeightPx - safetyMarginPx;
    if (top + height > currentPageEnd) {
      const gap = currentPageEnd + safetyMarginPx - top;
      if (gap > 0) {
        const spacer = document.createElement("div");
        spacer.className = "pdf-pagination-spacer";
        spacer.style.height = `${gap}px`;
        spacer.style.width = "100%";
        spacer.style.background = "transparent";
        spacer.setAttribute("aria-hidden", "true");
        block.parentNode.insertBefore(spacer, block);
      }
    }
  });
}

async function renderAndDownload(htmlInner, filename) {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-10000px";
  container.style.top = "0";
  container.style.background = "#fff";
  container.innerHTML = htmlInner;
  document.body.appendChild(container);
  try {
    const root = container.querySelector(".pdf-pat-root");
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch { /* no-op */ }
    }
    const imgs = Array.from(root.querySelectorAll("img"));
    await Promise.all(imgs.map(img => {
      if (img.complete && img.naturalHeight > 0) return Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      });
    }));
    await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 80)));
    optimizePagination(root);

    const worker = html2pdf()
      .from(root)
      .set({
        margin: PDF_MARGINS,
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: { scale: 4, useCORS: true, allowTaint: false, backgroundColor: "#ffffff", width: 1080, windowWidth: 1080, scrollX: 0, scrollY: 0, letterRendering: true, logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait", compress: true, putOnlyUsedFonts: true },
        pagebreak: {
          mode: ["css"],
          avoid: [".item-block", ".timeline-event", ".card", "tr", "h1", "h2", "h3"],
        },
      });
    await worker.save();
    return { filename };
  } finally {
    document.body.removeChild(container);
  }
}

// ============================================================================
// EXPORT 1: PDF por Cliente
// ============================================================================
export async function gerarPDFPatrimonioPorCliente({ cliente, items, movimentacoes = [] }) {
  const dataEmissao = new Date().toLocaleString("pt-BR");
  const dataHoje = new Date().toISOString().split("T")[0];
  const filename = `Patrimonio_Cliente_${sanitize(cliente?.nome || "cliente")}_${dataHoje}.pdf`;
  const url = typeof window !== "undefined"
    ? `${window.location.origin}/cadastros/patrimonio?filtro_cliente=${encodeURIComponent(cliente?.id || "")}`
    : "";
  const qr = await buildQR(url);

  const totalValor = (items || []).reduce((sum, it) => sum + (it.valor_aquisicao || 0), 0);

  const html = `
    ${baseStyles}
    <div class="pdf-pat-root">
      ${headerHtml("Patrimônio · Por Cliente", dataEmissao, qr)}
      <div class="contrato-info">
        <div><b>Cliente:</b> ${safeText(cliente?.nome || "—")}</div>
        <div><b>CPF/CNPJ:</b> ${safeText(cliente?.documento || "—")}</div>
        <div><b>Total de itens alocados:</b> ${(items || []).length}</div>
        <div><b>Valor total dos itens:</b> ${fmtCurrency(totalValor)}</div>
      </div>
      <h1>Itens do Patrimônio Alocados</h1>
      ${(items && items.length > 0) ? items.map(itemBlock).join("") : '<p style="font-size:14px;color:#616161;margin:12px 4px">Nenhum item alocado atualmente a este cliente.</p>'}
      <h2>Histórico Geral de Movimentações</h2>
      ${movimentacoesTable(movimentacoes)}
      <div class="footer">
        <span>Documento gerado em ${dataEmissao}</span>
        <span><b>OBRASUL-RJ</b> · Sistema de Gestão de Patrimônio</span>
      </div>
    </div>
  `;
  return await renderAndDownload(html, filename);
}

// ============================================================================
// EXPORT 2: PDF por Funcionário
// ============================================================================
export async function gerarPDFPatrimonioPorFuncionario({ funcionario, items, movimentacoes = [] }) {
  const dataEmissao = new Date().toLocaleString("pt-BR");
  const dataHoje = new Date().toISOString().split("T")[0];
  const filename = `Patrimonio_Funcionario_${sanitize(funcionario?.nome || "funcionario")}_${dataHoje}.pdf`;
  const url = typeof window !== "undefined"
    ? `${window.location.origin}/cadastros/patrimonio?filtro_funcionario=${encodeURIComponent(funcionario?.id || "")}`
    : "";
  const qr = await buildQR(url);
  const totalValor = (items || []).reduce((sum, it) => sum + (it.valor_aquisicao || 0), 0);

  const html = `
    ${baseStyles}
    <div class="pdf-pat-root">
      ${headerHtml("Patrimônio · Por Funcionário", dataEmissao, qr)}
      <div class="contrato-info">
        <div><b>Funcionário:</b> ${safeText(funcionario?.nome || "—")}</div>
        <div><b>Cargo:</b> ${safeText(funcionario?.cargo || "—")}</div>
        <div><b>Total de itens sob responsabilidade:</b> ${(items || []).length}</div>
        <div><b>Valor total:</b> ${fmtCurrency(totalValor)}</div>
      </div>
      <h1>Itens Sob Posse do Funcionário</h1>
      ${(items && items.length > 0) ? items.map(itemBlock).join("") : '<p style="font-size:14px;color:#616161;margin:12px 4px">Nenhum item alocado a este funcionário atualmente.</p>'}
      <h2>Histórico de Movimentações do Funcionário</h2>
      ${movimentacoesTable(movimentacoes)}
      <div class="footer">
        <span>Documento gerado em ${dataEmissao}</span>
        <span><b>OBRASUL-RJ</b> · Sistema de Gestão de Patrimônio</span>
      </div>
    </div>
  `;
  return await renderAndDownload(html, filename);
}

// ============================================================================
// EXPORT 3: Ficha individual do Patrimônio (linha do tempo completa)
// ============================================================================
export async function gerarPDFPatrimonioItem({ item, historico = [], reparos = [] }) {
  const dataEmissao = new Date().toLocaleString("pt-BR");
  const dataHoje = new Date().toISOString().split("T")[0];
  const filename = `Patrimonio_${sanitize(item?.codigo_patrimonio || "item")}_${sanitize(item?.nome || "")}_${dataHoje}.pdf`;
  const url = typeof window !== "undefined"
    ? `${window.location.origin}/cadastros/patrimonio?openId=${encodeURIComponent(item?.id || "")}`
    : "";
  const qr = await buildQR(url);

  // Construir linha do tempo unificada e ordenada por data
  const events = [];
  // Evento de criação/entrada
  events.push({
    tipo: "criacao",
    data: item?.created_at || item?.data_aquisicao,
    titulo: "Entrada no Sistema",
    detalhe: `Item cadastrado com código ${item?.codigo_patrimonio || "—"}. Valor de aquisição: ${fmtCurrency(item?.valor_aquisicao || 0)}. Fornecedor: ${safeText(item?.fornecedor || "—")}.`,
  });
  // Movimentações
  (historico || []).forEach(m => {
    const destino = m.cliente_info?.nome || m.funcionario_info?.nome || m.emprestado_para || m.localizacao_descricao || "—";
    events.push({
      tipo: "movimentacao",
      data: m.created_at || m.data_solicitacao,
      titulo: `Movimentação: ${SITUACAO_LABEL[m.tipo] || m.tipo}`,
      detalhe: `Situação anterior: ${SITUACAO_LABEL[m.situacao_anterior] || m.situacao_anterior || "—"} → ${SITUACAO_LABEL[m.tipo] || m.tipo}. Destino: ${safeText(destino)}. Responsável: ${safeText(m.responsavel_info?.nome || "—")}. ${m.motivo ? `Motivo: ${safeText(m.motivo)}.` : ""}`,
    });
  });
  // Reparos
  (reparos || []).forEach(r => {
    events.push({
      tipo: "reparo",
      data: r.data_inicio,
      titulo: `Reparo${r.data_fim ? ` (concluído em ${fmtDate(r.data_fim)})` : " em andamento"}`,
      detalhe: `${safeText(r.descricao || "—")}. Custo: ${fmtCurrency(r.custo || 0)}. Fornecedor: ${safeText(r.fornecedor_nome || "—")}. Responsável: ${safeText(r.responsavel_nome || "—")}.`,
    });
  });
  // Inativação
  if (item?.motivo_inativacao) {
    events.push({
      tipo: "inativacao",
      data: item.data_inativacao,
      titulo: "Item Inativado",
      detalhe: `Motivo: ${safeText(item.motivo_inativacao)}. ${item.observacoes_inativacao || ""}`,
    });
  }
  events.sort((a, b) => {
    const da = new Date(a.data || 0).getTime();
    const dbb = new Date(b.data || 0).getTime();
    return da - dbb;
  });

  const timelineHtml = events.map(e => `
    <div class="timeline-event ${e.tipo}">
      <div>
        <div class="te-data">${fmtDateTime(e.data)}</div>
      </div>
      <div>
        <div class="te-titulo">${safeText(e.titulo)}</div>
        <div class="te-detalhe">${safeText(fixLegacyText(e.detalhe))}</div>
      </div>
    </div>
  `).join("");

  const html = `
    ${baseStyles}
    <div class="pdf-pat-root">
      ${headerHtml(`Ficha · ${item?.codigo_patrimonio || "Patrimônio"}`, dataEmissao, qr)}
      <div class="contrato-info">
        <div><b>Código:</b> ${safeText(item?.codigo_patrimonio || "—")}</div>
        <div><b>Nome:</b> ${safeText(fixLegacyText(item?.nome || "—"))}</div>
        <div><b>Situação atual:</b> ${SITUACAO_LABEL[item?.situacao] || item?.situacao || "—"}</div>
        <div><b>Estado:</b> ${safeText(item?.estado_conservacao || "—")}</div>
        <div><b>Categoria:</b> ${safeText(item?.categoria || "—")}</div>
        <div><b>Marca/Modelo:</b> ${safeText(item?.marca || "—")} ${safeText(item?.modelo || "")}</div>
        <div><b>Data de aquisição:</b> ${fmtDate(item?.data_aquisicao)}</div>
        <div><b>Valor de aquisição:</b> ${fmtCurrency(item?.valor_aquisicao || 0)}</div>
        <div><b>Localização atual:</b> ${safeText(fixLegacyText(item?.localizacao_descricao || "—"))}</div>
        <div><b>Total de movimentações:</b> ${(historico || []).length}</div>
      </div>
      <h1>Ficha Técnica</h1>
      ${itemBlock(item || {})}
      <h2>Linha do Tempo Completa</h2>
      <div class="timeline">${timelineHtml}</div>
      <div class="footer">
        <span>Documento gerado em ${dataEmissao}</span>
        <span><b>OBRASUL-RJ</b> · Ficha oficial de patrimônio</span>
      </div>
    </div>
  `;
  return await renderAndDownload(html, filename);
}
